import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: Abstract Classes & Pure Virtual Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const abstractTabs = [
    {
        label: "Pure Virtual (= 0)",
        code: `#include <iostream>
#include <string>

// ── Abstract class — has at least one pure virtual function ───
// Cannot be instantiated directly
// Forces derived classes to provide an implementation

class Shape {                       // ABSTRACT CLASS
public:
    // ── Pure virtual: = 0 makes the function abstract ─────────
    virtual double area()        const = 0;   // MUST override
    virtual double perimeter()   const = 0;   // MUST override
    virtual std::string name()   const = 0;   // MUST override

    // ── Non-pure virtual — has default, may override ──────────
    virtual void describe() const {
        std::cout << name() << ": area=" << area()
                  << "  perimeter=" << perimeter() << "\\n";
    }

    // ── Non-virtual — cannot be overridden ────────────────────
    bool isLargerThan(const Shape& other) const {
        return area() > other.area();
    }

    virtual ~Shape() = default;    // ALWAYS virtual destructor!
};

// ── Concrete class: implements ALL pure virtuals ──────────────
class Circle : public Shape {
    double r;
public:
    Circle(double r) : r(r) {}
    double area()        const override { return 3.14159 * r * r; }
    double perimeter()   const override { return 2 * 3.14159 * r; }
    std::string name()   const override { return "Circle"; }
};

class Rectangle : public Shape {
    double w, h;
public:
    Rectangle(double w, double h) : w(w), h(h) {}
    double area()        const override { return w * h; }
    double perimeter()   const override { return 2*(w+h); }
    std::string name()   const override { return "Rectangle"; }
};

int main() {
    // Shape s;            // ❌ ERROR: cannot instantiate abstract class
    Circle    c(5);
    Rectangle r(4, 6);

    c.describe();          // Circle: area=78.54  perimeter=31.42
    r.describe();          // Rectangle: area=24  perimeter=20

    std::cout << c.isLargerThan(r);   // true — uses area() polymorphically
}`,
    },
    {
        label: "Abstract Base Classes",
        code: `#include <iostream>
#include <string>
#include <memory>

// ── Abstract base class as an INTERFACE contract ──────────────
// All pure virtual = pure interface (like Java/C# interface)

class Serializable {
public:
    virtual std::string serialize()             const = 0;
    virtual void        deserialize(const std::string& data) = 0;
    virtual ~Serializable() = default;
};

class Printable {
public:
    virtual void print(std::ostream& os) const = 0;
    virtual ~Printable() = default;
};

// ── Multiple abstract base classes ───────────────────────────
class Document : public Serializable, public Printable {
    std::string title;
    std::string content;
public:
    Document(const std::string& t, const std::string& c)
        : title(t), content(c) {}

    // Must implement ALL pure virtuals from BOTH bases:
    std::string serialize() const override {
        return title + "|" + content;
    }
    void deserialize(const std::string& data) override {
        auto sep = data.find('|');
        title   = data.substr(0, sep);
        content = data.substr(sep + 1);
    }
    void print(std::ostream& os) const override {
        os << "=== " << title << " ===\\n" << content << "\\n";
    }
};

int main() {
    Document doc("C++ Guide", "Learn about abstract classes");
    doc.print(std::cout);

    std::string data = doc.serialize();
    std::cout << "Serialized: " << data << "\\n";

    // Polymorphism via base pointers:
    std::unique_ptr<Serializable> s = std::make_unique<Document>("x","y");
    std::unique_ptr<Printable>    p = std::make_unique<Document>("a","b");
    s->serialize();
    p->print(std::cout);
}`,
    },
    {
        label: "Partially Abstract",
        code: `#include <iostream>
#include <string>

// ── Partially abstract — some virtual, some concrete ──────────
// Useful when most behaviour is shared but a few details vary

class Logger {
public:
    // ── Concrete implementation — shared by all loggers ───────
    void log(const std::string& msg) {
        std::string formatted = "[" + getLevel() + "] " + format(msg);
        write(formatted);   // calls the derived write
    }

    void logError(const std::string& msg) {
        log("ERROR: " + msg);
    }

    // ── Pure virtual — subclass decides HOW to write ──────────
    virtual void write(const std::string& line) = 0;

    // ── Virtual with default — subclass MAY customise ─────────
    virtual std::string getLevel() const { return "INFO"; }
    virtual std::string format(const std::string& msg) const {
        return msg;
    }

    virtual ~Logger() = default;
};

// ── Console logger — writes to stdout ─────────────────────────
class ConsoleLogger : public Logger {
public:
    void write(const std::string& line) override {
        std::cout << line << "\\n";
    }
};

// ── File logger — overrides write + format ────────────────────
class FileLogger : public Logger {
    std::string path;
public:
    FileLogger(const std::string& p) : path(p) {}
    void write(const std::string& line) override {
        // In real code: open file and append
        std::cout << "[FILE:" << path << "] " << line << "\\n";
    }
    std::string getLevel() const override { return "FILE"; }
};

int main() {
    ConsoleLogger cl;
    FileLogger    fl("app.log");
    cl.log("Application started");  // [INFO] Application started
    fl.log("Saved record");          // [FILE:app.log] [FILE] Saved record
}`,
    },
    {
        label: "= delete / = default",
        code: `#include <iostream>

// ── = default — explicitly request compiler-generated version ──
class Point {
    double x, y;
public:
    Point(double x, double y) : x(x), y(y) {}

    // Compiler won't generate copy/move once we define a constructor,
    // so explicitly request them:
    Point(const Point&)            = default;  // copy constructor
    Point& operator=(const Point&) = default;  // copy assignment
    Point(Point&&)                 = default;  // move constructor
    Point& operator=(Point&&)      = default;  // move assignment
    ~Point()                       = default;  // destructor

    void print() const { std::cout << "(" << x << "," << y << ")"; }
};

// ── = delete — explicitly forbid a function ────────────────────
class Singleton {
    static Singleton* instance;
    Singleton() {}                             // private constructor
public:
    Singleton(const Singleton&)            = delete;   // no copy
    Singleton& operator=(const Singleton&) = delete;   // no copy assign
    Singleton(Singleton&&)                 = delete;   // no move
    Singleton& operator=(Singleton&&)      = delete;   // no move assign

    static Singleton& getInstance() {
        if (!instance) instance = new Singleton();
        return *instance;
    }
    void doWork() { std::cout << "Working...\\n"; }
};
Singleton* Singleton::instance = nullptr;

// ── = delete on free functions ────────────────────────────────
void process(int x);
void process(double) = delete;   // prevent implicit int→double conversion
// process(3.14);  // ❌ COMPILE ERROR — deleted overload

int main() {
    Point p1(1, 2);
    Point p2 = p1;           // ✅ copy constructor
    // Singleton s;           // ❌ private constructor
    Singleton::getInstance().doWork();   // ✅ through getInstance()
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Interfaces via Pure-Virtual Classes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const interfaceTabs = [
    {
        label: "Defining Interfaces",
        code: `#include <iostream>
#include <string>
#include <vector>

// ── C++ "interface" = class with ALL pure virtual functions ───
// No data members. No implementation.
// Just a contract that implementors must fulfil.

class IDrawable {
public:
    virtual void draw()                    = 0;
    virtual void move(double dx, double dy) = 0;
    virtual void resize(double factor)      = 0;
    virtual ~IDrawable() = default;           // always!
};

class ISelectable {
public:
    virtual bool  contains(double x, double y) const = 0;
    virtual void  select()                           = 0;
    virtual void  deselect()                         = 0;
    virtual bool  isSelected()               const   = 0;
    virtual ~ISelectable() = default;
};

class ISerializable {
public:
    virtual std::string toJSON()                      const = 0;
    virtual void        fromJSON(const std::string& j)      = 0;
    virtual ~ISerializable() = default;
};

// ── Implement multiple interfaces ────────────────────────────
class UIWidget : public IDrawable,
                 public ISelectable,
                 public ISerializable {
    double x, y, w, h;
    bool   selected = false;
public:
    UIWidget(double x, double y, double w, double h)
        : x(x), y(y), w(w), h(h) {}

    void draw()                     override { std::cout << "Drawing widget at (" << x << "," << y << ")\\n"; }
    void move(double dx, double dy) override { x += dx; y += dy; }
    void resize(double f)           override { w *= f; h *= f; }

    bool contains(double px, double py) const override {
        return px >= x && px <= x+w && py >= y && py <= y+h;
    }
    void select()      override { selected = true;  }
    void deselect()    override { selected = false; }
    bool isSelected()  const override { return selected; }

    std::string toJSON() const override {
        return "{x:" + std::to_string(x) + ",y:" + std::to_string(y) + "}";
    }
    void fromJSON(const std::string&) override { /* parse */ }
};`,
    },
    {
        label: "Programming to Interfaces",
        code: `#include <iostream>
#include <vector>
#include <memory>
#include <string>

// ── Interface ─────────────────────────────────────────────────
class IAnimal {
public:
    virtual void speak()           const = 0;
    virtual std::string getName()  const = 0;
    virtual std::string getSound() const = 0;
    virtual ~IAnimal() = default;
};

class Dog  : public IAnimal {
public:
    void speak()           const override { std::cout << "Woof!\\n"; }
    std::string getName()  const override { return "Dog"; }
    std::string getSound() const override { return "woof"; }
};

class Cat  : public IAnimal {
public:
    void speak()           const override { std::cout << "Meow!\\n"; }
    std::string getName()  const override { return "Cat"; }
    std::string getSound() const override { return "meow"; }
};

class Duck : public IAnimal {
public:
    void speak()           const override { std::cout << "Quack!\\n"; }
    std::string getName()  const override { return "Duck"; }
    std::string getSound() const override { return "quack"; }
};

// ── Functions that accept the INTERFACE, not the concrete type ─
void makeAllSpeak(const std::vector<std::unique_ptr<IAnimal>>& animals) {
    for (const auto& a : animals) {
        std::cout << a->getName() << " says: ";
        a->speak();               // correct version called at runtime!
    }
}

int main() {
    std::vector<std::unique_ptr<IAnimal>> zoo;
    zoo.push_back(std::make_unique<Dog>());
    zoo.push_back(std::make_unique<Cat>());
    zoo.push_back(std::make_unique<Duck>());

    makeAllSpeak(zoo);
    // Dog says: Woof!
    // Cat says: Meow!
    // Duck says: Quack!

    // Can add new animal types WITHOUT modifying makeAllSpeak — Open/Closed!
}`,
    },
    {
        label: "Interface Segregation",
        code: `// ── Interface Segregation Principle (ISP) ───────────────────
// "Clients should not depend on interfaces they don't use"
// Split fat interfaces into focused, minimal ones

// ❌ FAT interface — forces irrelevant implementations:
class IBadWorker {
public:
    virtual void work()    = 0;
    virtual void eat()     = 0;   // robots don't eat!
    virtual void sleep()   = 0;   // robots don't sleep!
    virtual void charge()  = 0;   // humans don't charge!
};

// ✅ SEGREGATED interfaces — each focuses on one concern:
class IWorkable {
public:
    virtual void work() = 0;
    virtual ~IWorkable() = default;
};

class IEatable {
public:
    virtual void eat()   = 0;
    virtual void sleep() = 0;
    virtual ~IEatable() = default;
};

class IChargeable {
public:
    virtual void charge() = 0;
    virtual ~IChargeable() = default;
};

// Human needs work + eat — NOT charge:
class Human : public IWorkable, public IEatable {
public:
    void work()  override { std::cout << "Human working\\n"; }
    void eat()   override { std::cout << "Human eating\\n"; }
    void sleep() override { std::cout << "Human sleeping\\n"; }
};

// Robot needs work + charge — NOT eat:
class Robot : public IWorkable, public IChargeable {
public:
    void work()   override { std::cout << "Robot working\\n"; }
    void charge() override { std::cout << "Robot charging\\n"; }
};

// Factory function — only cares about IWorkable:
void runTask(IWorkable& worker) { worker.work(); }
// Works with BOTH Human and Robot without knowing the concrete type`,
    },
    {
        label: "Interface + Default Impl",
        code: `#include <iostream>
#include <string>
#include <vector>
#include <algorithm>

// ── Mixin: abstract base that provides SOME implementation ────
// Not fully abstract — gives helpers while requiring core overrides

class ICollection {
public:
    // ── Pure virtual core: MUST implement ─────────────────────
    virtual void   add(int item)  = 0;
    virtual bool   remove(int item) = 0;
    virtual size_t size()  const  = 0;
    virtual bool   contains(int item) const = 0;

    // ── Provided implementations built on the core ────────────
    bool isEmpty() const { return size() == 0; }

    void addAll(const std::vector<int>& items) {
        for (int item : items) add(item);   // calls derived add()
    }

    bool removeAll(const std::vector<int>& items) {
        bool changed = false;
        for (int item : items) changed |= remove(item);
        return changed;
    }

    bool containsAll(const std::vector<int>& items) const {
        return std::all_of(items.begin(), items.end(),
            [this](int i){ return contains(i); });
    }

    virtual ~ICollection() = default;
};

// ── Concrete implementation just fills in the core ────────────
class SortedSet : public ICollection {
    std::vector<int> data;
public:
    void add(int item) override {
        if (!contains(item)) {
            data.push_back(item);
            std::sort(data.begin(), data.end());
        }
    }
    bool remove(int item) override {
        auto it = std::find(data.begin(), data.end(), item);
        if (it == data.end()) return false;
        data.erase(it); return true;
    }
    size_t size()            const override { return data.size(); }
    bool   contains(int x)   const override {
        return std::binary_search(data.begin(), data.end(), x);
    }
};

int main() {
    SortedSet s;
    s.addAll({5, 3, 1, 4, 2});         // uses ICollection helper
    std::cout << s.isEmpty() << "\\n";  // false — from ICollection
    std::cout << s.containsAll({1,3,5}); // true
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Virtual Functions & vtable
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const vtableTabs = [
    {
        label: "virtual & override",
        code: `#include <iostream>

class Animal {
public:
    // ── virtual — enables runtime polymorphism ─────────────────
    virtual void speak() const {
        std::cout << "Animal speaks\\n";
    }

    // ── Non-virtual — statically dispatched (no polymorphism) ──
    void breathe() const {
        std::cout << "Animal breathes\\n";
    }

    virtual ~Animal() = default;
};

class Dog : public Animal {
public:
    // ── override — confirm we're overriding a virtual ──────────
    // Compiler error if base has no matching virtual (catches typos!)
    void speak() const override {
        std::cout << "Woof!\\n";
    }

    // ── Hiding (NOT overriding) non-virtual ───────────────────
    void breathe() const {             // hides Animal::breathe — not virtual!
        std::cout << "Dog breathes\\n";
    }
};

int main() {
    Dog d;
    Animal& ref = d;          // base reference to derived object

    ref.speak();               // Woof!  ← virtual dispatch → Dog::speak
    ref.breathe();             // Animal breathes ← static dispatch → Animal::breathe!
    d.breathe();               // Dog breathes ← direct call, no polymorphism needed

    // ── The difference matters when using base pointers ────────
    Animal* animals[] = { new Dog(), new Dog() };
    for (auto* a : animals) {
        a->speak();            // Woof! Woof! — virtual dispatch works
        a->breathe();          // Animal breathes — static, ignores runtime type
        delete a;
    }
}`,
    },
    {
        label: "vtable internals",
        code: `// ── How the vtable works (conceptually) ─────────────────────
//
//  Every class with virtual functions gets a VTABLE:
//  a compile-time array of function pointers.
//
//  Every OBJECT of that class stores a hidden VPTR (vtable pointer).
//
//  class Animal {
//      /*hidden*/ void** __vptr;     ← added by compiler (8 bytes)
//      // ... user data ...
//  };
//
//  Animal vtable:
//    [0] → Animal::speak()
//    [1] → Animal::~Animal()
//
//  Dog vtable:
//    [0] → Dog::speak()      ← REPLACED with Dog's version
//    [1] → Dog::~Dog()
//    [2] → Dog::fetch()      ← new entry for Dog-only method
//
//  Runtime dispatch:
//    Animal* a = new Dog();
//    a->speak();
//    ↓
//    a->__vptr[0]();   ← look up slot 0 in Dog's vtable → Dog::speak()
//
//  Cost: ONE indirection (pointer dereference) per virtual call
//  Usually negligible, but avoid in tight inner loops if profiling shows it

// ── sizeof shows the vptr overhead ────────────────────────────
struct NoVirtual { int x; };                   // sizeof = 4
struct WithVirtual { virtual void f(); int x; }; // sizeof = 16 (vptr + pad + x)

// ── Final classes/methods prevent virtual dispatch overhead ───
class FastMath final {    // final class: cannot be derived from
    int x;
public:
    virtual void compute() final;  // final method: cannot be overridden
    // compiler can de-virtualise calls → no vtable lookup!
};`,
    },
    {
        label: "virtual destructor",
        code: `#include <iostream>
#include <memory>

// ── WHY virtual destructors matter ────────────────────────────
// Without virtual destructor: deleting through base pointer
// only calls the BASE destructor → RESOURCE LEAK!

class BadBase {
public:
    ~BadBase() { std::cout << "BadBase::~BadBase()\\n"; }  // NOT virtual!
};

class BadDerived : public BadBase {
    int* data;
public:
    BadDerived() : data(new int[100]) {}
    ~BadDerived() {
        delete[] data;   // ← NEVER CALLED if deleted via BadBase*!
        std::cout << "BadDerived::~BadDerived()\\n";
    }
};

class GoodBase {
public:
    virtual ~GoodBase() {    // VIRTUAL destructor
        std::cout << "GoodBase::~GoodBase()\\n";
    }
};

class GoodDerived : public GoodBase {
    int* data;
public:
    GoodDerived() : data(new int[100]) {}
    ~GoodDerived() override {
        delete[] data;   // ← ALWAYS called because destructor is virtual
        std::cout << "GoodDerived::~GoodDerived()\\n";
    }
};

int main() {
    // ❌ Memory leak — BadDerived::~BadDerived() NOT called:
    BadBase* bad = new BadDerived();
    delete bad;      // only BadBase::~BadBase() runs!

    // ✅ Correct — GoodDerived::~GoodDerived() IS called:
    GoodBase* good = new GoodDerived();
    delete good;     // ~GoodDerived() then ~GoodBase() — correct order

    // ✅ Smart pointers are safer:
    auto ptr = std::make_unique<GoodDerived>();
    // unique_ptr calls the correct destructor automatically
}`,
    },
    {
        label: "final & override",
        code: `#include <iostream>

class Base {
public:
    virtual void compute()  { std::cout << "Base::compute()\\n"; }
    virtual void render()   { std::cout << "Base::render()\\n"; }
    virtual void update()   { std::cout << "Base::update()\\n"; }
    virtual ~Base() = default;
};

class Mid : public Base {
public:
    void compute() override final {   // ← FINAL: no further override!
        std::cout << "Mid::compute() — locked\\n";
    }
    void render()  override { std::cout << "Mid::render()\\n"; }
};

class Leaf : public Mid {
public:
    // void compute() override { }   // ❌ COMPILE ERROR — compute is final!
    void render()  override { std::cout << "Leaf::render()\\n"; }
    void update()  override { std::cout << "Leaf::update()\\n"; }
};

// ── final class — cannot be inherited ─────────────────────────
class Immutable final : public Base {
public:
    void compute() override { std::cout << "Immutable::compute()\\n"; }
};

// class Child : public Immutable {};  // ❌ COMPILE ERROR — Immutable is final

// ── Why final matters ─────────────────────────────────────────
// 1. Compiler can de-virtualise calls to final methods (faster)
// 2. Communicates design intent: "this class/method is complete"
// 3. Prevents unintended extension that might break invariants

int main() {
    Leaf l;
    Base& b = l;
    b.compute();   // Mid::compute() — locked
    b.render();    // Leaf::render()
    b.update();    // Leaf::update()
}`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: Design Patterns using Interfaces
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const patternTabs = [
    {
        label: "Strategy Pattern",
        code: `#include <iostream>
#include <memory>
#include <vector>
#include <algorithm>

// ── Strategy: swap algorithms at runtime via interface ────────
class ISortStrategy {
public:
    virtual void sort(std::vector<int>& data) = 0;
    virtual std::string name() const = 0;
    virtual ~ISortStrategy() = default;
};

class BubbleSort : public ISortStrategy {
public:
    void sort(std::vector<int>& v) override {
        for (size_t i = 0; i < v.size(); ++i)
            for (size_t j = 0; j + 1 < v.size() - i; ++j)
                if (v[j] > v[j+1]) std::swap(v[j], v[j+1]);
    }
    std::string name() const override { return "BubbleSort"; }
};

class QuickSort : public ISortStrategy {
public:
    void sort(std::vector<int>& v) override {
        std::sort(v.begin(), v.end());   // std::sort uses introsort
    }
    std::string name() const override { return "QuickSort"; }
};

// ── Context: owns a strategy, delegates sorting to it ─────────
class Sorter {
    std::unique_ptr<ISortStrategy> strategy;
public:
    explicit Sorter(std::unique_ptr<ISortStrategy> s)
        : strategy(std::move(s)) {}

    void setStrategy(std::unique_ptr<ISortStrategy> s) {
        strategy = std::move(s);         // swap strategy at runtime!
    }

    void sort(std::vector<int>& data) {
        std::cout << "Using " << strategy->name() << "\\n";
        strategy->sort(data);
    }
};

int main() {
    std::vector<int> data = {5, 2, 8, 1, 9};
    Sorter sorter(std::make_unique<BubbleSort>());
    sorter.sort(data);                           // Using BubbleSort

    sorter.setStrategy(std::make_unique<QuickSort>());
    sorter.sort(data);                           // Using QuickSort
}`,
    },
    {
        label: "Template Method Pattern",
        code: `#include <iostream>
#include <string>

// ── Template Method: define algorithm skeleton, fill in steps ─
// Abstract base controls the STRUCTURE; derived fill in DETAILS

class DataProcessor {
public:
    // ── Template method: FINAL algorithm skeleton ─────────────
    void process(const std::string& source) {
        auto raw  = readData(source);    // step 1 — abstract
        auto clean = validateData(raw);  // step 2 — has default
        auto result = processData(clean);// step 3 — abstract
        writeOutput(result);             // step 4 — abstract
        onComplete();                    // step 5 — hook (optional)
    }

    virtual ~DataProcessor() = default;

protected:
    // ── Pure virtual: MUST implement ──────────────────────────
    virtual std::string readData(const std::string& source) = 0;
    virtual std::string processData(const std::string& data) = 0;
    virtual void        writeOutput(const std::string& result) = 0;

    // ── Virtual hook: MAY override ─────────────────────────────
    virtual std::string validateData(const std::string& data) {
        return data;    // default: no validation
    }
    virtual void onComplete() {}  // empty hook — override if needed
};

class CSVProcessor : public DataProcessor {
protected:
    std::string readData(const std::string& src) override {
        return "col1,col2,col3\\n1,2,3\\n4,5,6";
    }
    std::string processData(const std::string& data) override {
        return "Processed " + std::to_string(data.size()) + " bytes of CSV";
    }
    void writeOutput(const std::string& result) override {
        std::cout << "[CSV] " << result << "\\n";
    }
    void onComplete() override {
        std::cout << "[CSV] Done — file closed.\\n";
    }
};

int main() {
    CSVProcessor proc;
    proc.process("data.csv");
    // [CSV] Processed 28 bytes of CSV
    // [CSV] Done — file closed.
}`,
    },
    {
        label: "Observer Pattern",
        code: `#include <iostream>
#include <vector>
#include <memory>
#include <string>
#include <algorithm>

// ── Observer: one subject notifies many listeners ─────────────
class IObserver {
public:
    virtual void onEvent(const std::string& event, int value) = 0;
    virtual ~IObserver() = default;
};

class ISubject {
public:
    virtual void subscribe(std::shared_ptr<IObserver> obs)   = 0;
    virtual void unsubscribe(std::shared_ptr<IObserver> obs) = 0;
    virtual void notify(const std::string& event, int val)   = 0;
    virtual ~ISubject() = default;
};

// ── Concrete subject: Stock price tracker ─────────────────────
class Stock : public ISubject {
    std::string ticker;
    int price;
    std::vector<std::shared_ptr<IObserver>> observers;
public:
    Stock(const std::string& t, int p) : ticker(t), price(p) {}

    void subscribe(std::shared_ptr<IObserver> obs)   override { observers.push_back(obs); }
    void unsubscribe(std::shared_ptr<IObserver> obs) override {
        observers.erase(std::remove(observers.begin(), observers.end(), obs), observers.end());
    }
    void notify(const std::string& event, int val) override {
        for (auto& o : observers) o->onEvent(ticker + ":" + event, val);
    }

    void setPrice(int p) {
        price = p;
        notify("PRICE_CHANGE", p);  // broadcast to all observers
    }
};

class AlertObserver : public IObserver {
    std::string name;
public:
    AlertObserver(const std::string& n) : name(n) {}
    void onEvent(const std::string& ev, int val) override {
        std::cout << "[" << name << "] Event: " << ev << " = " << val << "\\n";
    }
};

int main() {
    auto stock = std::make_shared<Stock>("AAPL", 150);
    auto alert1 = std::make_shared<AlertObserver>("Trader A");
    auto alert2 = std::make_shared<AlertObserver>("Trader B");
    stock->subscribe(alert1);
    stock->subscribe(alert2);
    stock->setPrice(155);   // both observers notified
    stock->unsubscribe(alert1);
    stock->setPrice(160);   // only Trader B notified
}`,
    },
    {
        label: "Factory Pattern",
        code: `#include <iostream>
#include <memory>
#include <string>
#include <stdexcept>

// ── Factory: create objects through an interface ──────────────
class IButton {
public:
    virtual void render() = 0;
    virtual void onClick() = 0;
    virtual ~IButton() = default;
};

class ICheckbox {
public:
    virtual void render() = 0;
    virtual void toggle() = 0;
    virtual ~ICheckbox() = default;
};

// ── Abstract factory: creates FAMILIES of related objects ─────
class IGUIFactory {
public:
    virtual std::unique_ptr<IButton>   createButton()   = 0;
    virtual std::unique_ptr<ICheckbox> createCheckbox() = 0;
    virtual ~IGUIFactory() = default;
};

// ── Windows theme family ───────────────────────────────────────
class WinButton : public IButton {
public:
    void render()  override { std::cout << "[Win] Drawing button\\n"; }
    void onClick() override { std::cout << "[Win] Click!\\n"; }
};
class WinCheckbox : public ICheckbox {
public:
    void render()  override { std::cout << "[Win] Drawing checkbox\\n"; }
    void toggle()  override { std::cout << "[Win] Toggle!\\n"; }
};
class WinFactory : public IGUIFactory {
public:
    std::unique_ptr<IButton>   createButton()   override { return std::make_unique<WinButton>(); }
    std::unique_ptr<ICheckbox> createCheckbox() override { return std::make_unique<WinCheckbox>(); }
};

// ── macOS theme family ────────────────────────────────────────
class MacButton : public IButton {
public:
    void render()  override { std::cout << "[Mac] Drawing button\\n"; }
    void onClick() override { std::cout << "[Mac] Click!\\n"; }
};
class MacFactory : public IGUIFactory {
public:
    std::unique_ptr<IButton>   createButton()   override { return std::make_unique<MacButton>(); }
    std::unique_ptr<ICheckbox> createCheckbox() override { return std::make_unique<WinCheckbox>(); }
};

// ── Application uses ONLY the factory interface ───────────────
void buildUI(IGUIFactory& factory) {
    auto btn = factory.createButton();
    auto chk = factory.createCheckbox();
    btn->render();  chk->render();  btn->onClick();
}

int main() {
    WinFactory win; buildUI(win);   // Windows-themed UI
    MacFactory mac; buildUI(mac);   // macOS-themed UI
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: CRTP (Static Polymorphism)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const crtpTabs = [
    {
        label: "CRTP Basics",
        code: `#include <iostream>

// ── CRTP — Curiously Recurring Template Pattern ───────────────
// Static polymorphism: resolved at COMPILE TIME, no vtable cost
// Pattern: class Derived : public Base<Derived>

template<typename Derived>
class Shape {
public:
    // ── Base calls derived via static_cast — zero runtime cost ─
    double area()      const { return derived().areaImpl(); }
    double perimeter() const { return derived().perimeterImpl(); }

    void describe() const {
        std::cout << typeid(Derived).name()
                  << ": area=" << area()
                  << "  perimeter=" << perimeter() << "\\n";
    }

private:
    // Safe downcast: known at compile time
    const Derived& derived() const { return static_cast<const Derived&>(*this); }
};

class Circle : public Shape<Circle> {
    double r;
public:
    Circle(double r) : r(r) {}
    double areaImpl()      const { return 3.14159 * r * r; }
    double perimeterImpl() const { return 2 * 3.14159 * r; }
};

class Rectangle : public Shape<Rectangle> {
    double w, h;
public:
    Rectangle(double w, double h) : w(w), h(h) {}
    double areaImpl()      const { return w * h; }
    double perimeterImpl() const { return 2*(w+h); }
};

int main() {
    Circle    c(5);
    Rectangle r(4, 6);

    c.describe();    // Circle: area=78.54  perimeter=31.42
    r.describe();    // Rectangle: area=24  perimeter=20

    // ── No virtual dispatch — all resolved at compile time ────
    // Faster than virtual in tight loops.
    // Trade-off: can't store Circle and Rectangle in same container
    //             (they're different types: Shape<Circle> vs Shape<Rectangle>)
}`,
    },
    {
        label: "CRTP Mixin",
        code: `#include <iostream>
#include <string>

// ── CRTP Mixin: add capabilities to a class without inheritance chains ─

template<typename Derived>
class Printable {
public:
    void print() const {
        std::cout << derived().toString() << "\\n";
    }
    void printLine(const std::string& prefix) const {
        std::cout << prefix << derived().toString() << "\\n";
    }
private:
    const Derived& derived() const { return static_cast<const Derived&>(*this); }
};

template<typename Derived>
class Comparable {
public:
    bool operator!=(const Derived& o) const { return !(derived() == o); }
    bool operator> (const Derived& o) const { return o < derived(); }
    bool operator<=(const Derived& o) const { return !(derived() > o); }
    bool operator>=(const Derived& o) const { return !(derived() < o); }
    // Only need to define == and < in Derived — the rest come free!
private:
    const Derived& derived() const { return static_cast<const Derived&>(*this); }
};

// ── Mix in both capabilities ───────────────────────────────────
class Temperature : public Printable<Temperature>,
                    public Comparable<Temperature> {
    double celsius;
public:
    Temperature(double c) : celsius(c) {}
    std::string toString() const { return std::to_string(celsius) + "°C"; }
    bool operator==(const Temperature& o) const { return celsius == o.celsius; }
    bool operator< (const Temperature& o) const { return celsius <  o.celsius; }
};

int main() {
    Temperature t1(20), t2(37);
    t1.print();              // 20.000000°C
    t2.printLine("Body: "); // Body: 37.000000°C
    std::cout << (t1 < t2)  << "\\n";  // 1 (true)
    std::cout << (t1 > t2)  << "\\n";  // 0 (false)
    std::cout << (t1 != t2) << "\\n";  // 1 (true)
}`,
    },
    {
        label: "Virtual vs CRTP",
        code: `// ── Virtual Polymorphism vs CRTP — when to use which ─────────

// ── Virtual (runtime polymorphism) ────────────────────────────
//   ✅ Objects of different types in the SAME container
//   ✅ Type not known until runtime (plugins, config-driven)
//   ✅ Simpler, more readable code
//   ✅ Can swap implementations at runtime
//   ❌ vtable overhead per call (~1-5ns)
//   ❌ Prevents inlining
//   ❌ Harder for CPU branch predictor
//
//   Use when: heterogeneous collections, plugins, UI, game entities

// ── CRTP (compile-time polymorphism) ─────────────────────────
//   ✅ Zero virtual dispatch overhead
//   ✅ Full inlining by compiler
//   ✅ Type safety at compile time
//   ✅ Mixin composition (add methods without deep hierarchies)
//   ❌ Objects of different types CANNOT share a container
//   ❌ Harder to read and reason about
//   ❌ Code bloat (each instantiation is a new type)
//   ❌ Cannot swap implementation at runtime
//
//   Use when: numeric types, math libraries, policy-based design,
//             performance-critical inner loops, mixins

// ── Quick comparison ──────────────────────────────────────────
//
//  Scenario                          Virtual    CRTP
//  -------------------------------------------------
//  vector<Shape*> of mixed shapes    ✅ yes     ❌ no
//  Single type, max performance      ❌ slow    ✅ yes
//  Plugin loaded at runtime          ✅ yes     ❌ no
//  Add behaviour via mixin           ⚠️ complex ✅ yes
//  Readable to C++ beginners         ✅ yes     ❌ complex`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COMPARISON TABLE & CHEATSHEET
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const keywordRows = [
    { kw: "virtual f()",        meaning: "Enables runtime polymorphism. Derived classes may override.", use: "Any method that should behave differently in subclasses" },
    { kw: "virtual f() = 0",    meaning: "Pure virtual — makes class abstract. Derived MUST override.", use: "Define a contract / interface; prevent base instantiation" },
    { kw: "override",           meaning: "Confirm overriding a base virtual. Compile error if no match.", use: "Every override in derived class — prevents silent hiding bugs" },
    { kw: "final (method)",     meaning: "No further override allowed. Compiler can de-virtualise.", use: "Lock down a method; performance-critical hot paths" },
    { kw: "final (class)",      meaning: "Class cannot be inherited. Enables devirtualisation.", use: "Utility/value classes not designed for extension" },
    { kw: "virtual ~Dtor()",    meaning: "Virtual destructor — ensures correct destructor chain on delete.", use: "Any base class with virtual methods — ALWAYS add this" },
    { kw: "= default",          meaning: "Request compiler-generated version of special member function.", use: "Restore defaults after defining other constructors" },
    { kw: "= delete",           meaning: "Explicitly forbid a function (copy, move, conversion, etc.).", use: "Singletons, non-copyable resources, prevent implicit conversions" },
];

const cheatBlocks = [
    {
        title: "Abstract Classes", color: "#f0883e",
        items: [
            "✅ At least one pure virtual (= 0) makes a class abstract",
            "✅ Can mix pure virtual and concrete methods",
            "✅ Protected constructors for base-only classes",
            "✅ ALWAYS declare virtual destructor in base class",
            "✅ Use override on every derived override",
            "❌ Cannot instantiate an abstract class directly",
            "❌ Don't forget to implement ALL pure virtuals in derived",
            "❌ Never throw from destructors (mark noexcept)",
        ],
    },
    {
        title: "Interfaces", color: "#58a6ff",
        items: [
            "✅ ALL pure virtual = interface (no data, no impl)",
            "✅ Prefer multiple small interfaces (ISP principle)",
            "✅ Program to the interface, not the implementation",
            "✅ Use unique_ptr<IFace> for polymorphic ownership",
            "✅ Accept IFace& or IFace* in functions (not concrete type)",
            "❌ Don't add data members to interfaces",
            "❌ Don't forget virtual ~IFace() = default",
            "❌ Don't bloat interfaces — split by client need",
        ],
    },
    {
        title: "Virtual Functions", color: "#7ee787",
        items: [
            "✅ virtual in base, override in derived — every time",
            "✅ virtual destructor if any method is virtual",
            "✅ Use final to lock methods or entire classes",
            "✅ Covariant return types are allowed in overrides",
            "❌ Never call virtual methods in constructors/destructors",
            "❌ Non-virtual method hiding is NOT polymorphism",
            "❌ Avoid virtual in performance-critical inner loops",
            "❌ Don't override without the override keyword",
        ],
    },
    {
        title: "Common Mistakes", color: "#f85149",
        items: [
            "❌ Missing virtual destructor → resource leak on delete",
            "❌ override typo without keyword → silent method hiding",
            "❌ Forgetting to implement a pure virtual → abstract derived",
            "❌ Calling virtual methods in constructor (not dispatched!)",
            "❌ Slicing: copying derived to base value loses vtable",
            "❌ delete via base ptr without virtual dtor → UB",
            "❌ Thinking non-virtual override = polymorphism",
            "❌ Fat interface with unrelated methods (violates ISP)",
        ],
    },
];

const designPrinciples = [
    { name: "Open/Closed",       abbr: "OCP", color: "#f0883e", desc: "Classes open for extension, closed for modification. Add behaviour by implementing the interface, not editing the class." },
    { name: "Liskov Substitution", abbr: "LSP", color: "#58a6ff", desc: "Derived class objects must be substitutable for base class objects. Overrides must honour the base contract." },
    { name: "Interface Segregation", abbr: "ISP", color: "#7ee787", desc: "Many small specific interfaces > one large general interface. Clients only depend on what they actually use." },
    { name: "Dependency Inversion", abbr: "DIP", color: "#a855f7", desc: "Depend on abstractions (interfaces), not concrete classes. High-level modules define interfaces; low-level implement them." },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppInterfacesAbstract() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [absTab,     setAbsTab]     = useState(0);
    const [ifaceTab,   setIfaceTab]   = useState(0);
    const [vtabTab,    setVtabTab]    = useState(0);
    const [patTab,     setPatTab]     = useState(0);
    const [crtpTab,    setCrtpTab]    = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ INTERFACES &amp; ABSTRACT CLASSES
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    pure virtual · abstract classes · interfaces · vtable · CRTP · design patterns
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["virtual = 0","Abstract Class","Interface","override/final","vtable","CRTP","Strategy","Observer"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        C++ has no <code style={{ color: "goldenrod" }}>interface</code> keyword — instead, a class
                        with <strong style={{ color: "goldenrod" }}>all pure virtual functions</strong> acts as an interface.
                        An <strong style={{ color: "goldenrod" }}>abstract class</strong> mixes pure virtuals with
                        concrete methods. Together they enable <strong style={{ color: "goldenrod" }}>runtime polymorphism</strong>,
                        SOLID design, and extensible architectures.
                    </Typography>
                </Box>
            </Box>

            {/* ── Abstract vs Interface overview cards ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                    {[
                        { icon: "🔒", title: "Abstract Class",  color: "#f0883e", points: ["Has ≥1 pure virtual (= 0)", "Can have data members", "Can have concrete methods", "Cannot be instantiated"] },
                        { icon: "📋", title: "Interface",        color: "#58a6ff", points: ["All methods pure virtual", "No data members", "No implementation", "Defines a contract only"] },
                        { icon: "⚡", title: "CRTP",             color: "#7ee787", points: ["Template-based", "Compile-time dispatch", "Zero vtable overhead", "No shared base pointer"] },
                    ].map((card, i) => (
                        <Box key={i} sx={{ bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${card.color}44`, borderTop: `3px solid ${card.color}`, borderRadius: "14px", p: "16px 18px" }}>
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
                                <Typography sx={{ fontSize: "1.4rem" }}>{card.icon}</Typography>
                                <Typography sx={{ fontWeight: 800, color: card.color, fontSize: "0.9rem" }}>{card.title}</Typography>
                            </Box>
                            {card.points.map((pt, j) => (
                                <Typography key={j} sx={{ fontSize: "0.8rem", opacity: 0.75, lineHeight: 1.7 }}>• {pt}</Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — ABSTRACT CLASSES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Abstract Classes &amp; Pure Virtual Functions
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>pure virtual function</strong> (<code style={{ color: "goldenrod" }}>= 0</code>) forces
                            derived classes to provide an implementation. Any class with at least one
                            pure virtual is <strong>abstract</strong> and cannot be instantiated directly.
                        </Typography>

                        <ConceptCard title="Abstract Class Anatomy" color="#f0883e">
                            <CodeBlock>{`class Shape {                     ← abstract class (has pure virtual)
public:
    virtual double area() const = 0;  ← pure virtual: MUST override
    virtual double perimeter() const = 0;

    virtual void describe() const {   ← non-pure virtual: has default, MAY override
        std::cout << area() << "\\n";
    }

    bool isValid() const {            ← non-virtual: cannot override
        return area() > 0;
    }

    virtual ~Shape() = default;       ← ALWAYS virtual destructor!
};

// Shape s;                           ← ❌ ERROR: abstract class
// Shape* s = new Shape();            ← ❌ ERROR: abstract class
Shape* s = new Circle(5);            ← ✅ OK: pointer to concrete derived`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={absTab} onChange={(_, v) => setAbsTab(v)} tabs={abstractTabs.map(t => t.label)} />
                        <CodeBlock>{abstractTabs[absTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — INTERFACES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Interfaces via Pure-Virtual Classes
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            C++ simulates interfaces with classes that have <em>only</em> pure virtual
                            functions — no data, no implementation. A class can implement multiple
                            interfaces, enabling flexible, composable contracts.
                        </Typography>

                        <ConceptCard title="Interface vs Abstract Class" color="#58a6ff">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                                {[
                                    { label: "Interface (pure)", color: "#58a6ff", code: `class IDrawable {
public:
  virtual void draw()   = 0;
  virtual void move(double dx,
                    double dy) = 0;
  virtual ~IDrawable()  = default;
  // No data. No implementation.
};` },
                                    { label: "Abstract Class", color: "#f0883e", code: `class Shape {
public:
  virtual double area() const = 0; // pure
  virtual void   draw();           // concrete
  double scale = 1.0;              // data ok!
  virtual ~Shape() = default;
};` },
                                ].map((item, j) => (
                                    <Box key={j} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${item.color}33`, borderRadius: "10px", overflow: "hidden" }}>
                                        <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 0.75, borderBottom: `1px solid ${item.color}22` }}>
                                            <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.8rem" }}>{item.label}</Typography>
                                        </Box>
                                        <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.78rem", p: "10px 14px", "&::before": { content: '""' } }}>{item.code}</CodeBlock>
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={ifaceTab} onChange={(_, v) => setIfaceTab(v)} tabs={interfaceTabs.map(t => t.label)} />
                        <CodeBlock>{interfaceTabs[ifaceTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — VIRTUAL FUNCTIONS & VTABLE
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Virtual Functions &amp; the vtable
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            The <strong>vtable</strong> (virtual dispatch table) is how C++ implements
                            runtime polymorphism. Each class with virtual functions gets a hidden
                            pointer (vptr) to a compile-time table of function pointers. Calling
                            a virtual method does one extra pointer dereference.
                        </Typography>

                        <StyledTabs value={vtabTab} onChange={(_, v) => setVtabTab(v)} tabs={vtableTabs.map(t => t.label)} />
                        <CodeBlock>{vtableTabs[vtabTab].code}</CodeBlock>

                        {/* Keyword reference table */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Virtual Keyword Reference" subtitle="Use all four consistently on every class hierarchy" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Keyword / Syntax", "Meaning", "When to Use"]} cols="1.5fr 2fr 2fr" />}
                            {keywordRows.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.85rem", mb: 0.5 }}>{row.kw}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)", mb: 0.4 }}>{row.meaning}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)" }}>{row.use}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 2fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.8rem" }}>{row.kw}</Box>
                                        <Box sx={{ fontSize: "0.81rem", opacity: 0.75, lineHeight: 1.6 }}>{row.meaning}</Box>
                                        <Box sx={{ fontSize: "0.79rem", opacity: 0.55, lineHeight: 1.6 }}>{row.use}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — DESIGN PATTERNS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. Design Patterns Using Interfaces
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Interfaces are the backbone of classic GoF design patterns.
                            They decouple the <em>what</em> (the interface contract) from the
                            <em> how</em> (the concrete implementation), making code open
                            to extension without modification.
                        </Typography>

                        <StyledTabs value={patTab} onChange={(_, v) => setPatTab(v)} tabs={patternTabs.map(t => t.label)} />
                        <CodeBlock>{patternTabs[patTab].code}</CodeBlock>

                        {/* SOLID principles grid */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🏛️</span>} title="SOLID Principles Enabled by Interfaces" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {designPrinciples.map((p, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${p.color}33`, borderLeft: `3px solid ${p.color}`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 0.75 }}>
                                        <PillChip label={p.abbr} color={p.color} />
                                        <Typography sx={{ fontWeight: 700, color: p.color, fontSize: "0.85rem" }}>{p.name}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.72, lineHeight: 1.7 }}>{p.desc}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — CRTP
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. CRTP — Static Polymorphism
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            The <strong>Curiously Recurring Template Pattern</strong> achieves polymorphism
                            at compile time — no vtable, no runtime overhead. The base template calls
                            methods on the derived class via <code style={{ color: "goldenrod" }}>static_cast</code>,
                            resolved entirely by the compiler.
                        </Typography>

                        <ConceptCard title="CRTP Pattern Structure" color="#7ee787">
                            <CodeBlock>{`template<typename Derived>        ← base is templated on its own child
class Base {
    void interface() {
        // calls derived method — resolved at COMPILE TIME:
        static_cast<Derived*>(this)->implementation();
    }
};

class Derived : public Base<Derived> {  ← passes itself as template arg
    void implementation() { ... }       ← this is what Base calls
};

//  Runtime polymorphism:   Base* b = new Derived();   b->virtual_fn();
//  CRTP:                   Derived d;   d.interface();   ← no vtable!`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={crtpTab} onChange={(_, v) => setCrtpTab(v)} tabs={crtpTabs.map(t => t.label)} />
                        <CodeBlock>{crtpTabs[crtpTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Interfaces &amp; Abstract Classes Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3, mb: 4 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Syntax quick reference */}
                        <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Syntax Quick Reference
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { title: "Define an interface", color: "#58a6ff", code: `class IFoo {
public:
  virtual void doX()       = 0;
  virtual int  getY() const = 0;
  virtual ~IFoo() = default; // required!
};` },
                                { title: "Implement interface", color: "#7ee787", code: `class Bar : public IFoo {
public:
  void doX()       override { ... }
  int  getY() const override { return 42; }
};
// IFoo* f = new Bar(); ← polymorphism` },
                                { title: "Abstract base class", color: "#f0883e", code: `class Animal {
  std::string name;  // data allowed
public:
  virtual void speak() const = 0; // pure
  virtual void move()  { ... }    // default
  virtual ~Animal() = default;
};` },
                                { title: "CRTP base", color: "#a855f7", code: `template<typename D>
class Base {
  void run() {
    static_cast<D*>(this)->impl();
  }
};
class Derived : public Base<Derived> {
  void impl() { ... }
};` },
                            ].map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderRadius: "12px", overflow: "hidden" }}>
                                    <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 1, borderBottom: `1px solid ${item.color}22` }}>
                                        <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.82rem" }}>{item.title}</Typography>
                                    </Box>
                                    <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.79rem", p: "12px 16px", "&::before": { content: '""' } }}>
                                        {item.code}
                                    </CodeBlock>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
